import { DashboardLayout } from '@/components/layout';
import { useAuth } from '@/features/auth';
import { useEffectiveBusinessId } from '@/hooks';
import { useBusinessContextStore } from '@/features/business-context';
import { useProjects } from '@/features/projects';
// import { DashboardStats } from '../components/DashboardStats';
import { RecentProjects } from '../components/RecentProjects';
import { QuickActions } from '../components/QuickActions';
// import { useDashboardStats } from '../hooks/useDashboardStats';

export function DashboardPage() {
  const { profile } = useAuth();
  const effectiveBusinessId = useEffectiveBusinessId();
  const selectedBusinessName = useBusinessContextStore(
    (state) => state.selectedBusinessName
  );

  const isSuperAdmin = profile?.role === 'super_admin';

  // Fetch dashboard stats (commented out for now)
  // const { stats, isLoading: isLoadingStats } = useDashboardStats(effectiveBusinessId);

  // Fetch recent projects (sorted by created_at desc)
  const { projects, isLoading: isLoadingProjects } = useProjects({
    businessId: effectiveBusinessId,
    autoFetch: true,
  });

  // Check if business is selected (for super admins)
  const showBusinessWarning = isSuperAdmin && !effectiveBusinessId;

  // Get greeting based on time of day
  const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get first name for greeting
  const firstName = profile?.full_name?.split(' ')[0] || 'there';

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            {getGreeting()}, {firstName}!
          </h1>
          <p className="text-secondary-600 mt-1">
            Here's what's happening with your business
            {isSuperAdmin && selectedBusinessName && (
              <span className="text-primary-600"> • {selectedBusinessName}</span>
            )}
          </p>
        </div>

        {/* Business Warning for Super Admins */}
        {showBusinessWarning && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-amber-500 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-medium text-amber-800">No Business Selected</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Please select a business from the dropdown in the navigation bar
                  to view dashboard data.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Content */}
        {!showBusinessWarning && (
          <>
            {/* Quick Actions */}
            <QuickActions />

            {/* Stats Section (commented out for now) */}
            {/* <DashboardStats stats={stats} isLoading={isLoadingStats} /> */}

            {/* Recent Projects */}
            <RecentProjects
              projects={projects}
              isLoading={isLoadingProjects}
              maxDisplay={6}
            />
          </>
        )}

        {/* Empty state for super admin without business selected */}
        {showBusinessWarning && (
          <div className="text-center py-12 bg-white rounded-xl border border-secondary-200">
            <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-secondary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-secondary-900">
              Select a Business
            </h3>
            <p className="text-secondary-600 mt-1 max-w-md mx-auto">
              As a super admin, you can view data for any business. 
              Select one from the navigation bar to get started.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}